import { useCallback, useEffect, useState } from 'react';
import {
  Alert, Badge, Button, Form, FormGroup, Input, Label, Modal, ModalBody, ModalFooter,
  ModalHeader, Spinner, Table,
} from 'reactstrap';
import { api } from '../../api/client.js';
import { useAuth } from '../../auth/useAuth.js';

const emptyUser = { firstName: '', lastName: '', email: '', password: '', isAdmin: false };

export function AdminUsers() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setUsers(await api.admin.users());
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function submitCreate(event) {
    event.preventDefault();
    setBusy(true);
    try {
      await api.admin.createUser(creating);
      setCreating(null);
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  async function submitEdit(event) {
    event.preventDefault();
    setBusy(true);
    try {
      const { id, ...payload } = editing;
      if (!payload.newPassword) delete payload.newPassword;
      await api.admin.updateUser(id, payload);
      setEditing(null);
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  async function remove(user) {
    if (!window.confirm(`Delete ${user.displayName}? Their picks and results go with them.`)) return;
    try {
      await api.admin.deleteUser(user.id);
      await load();
    } catch (err) {
      setError(err.message);
    }
  }

  if (loading) return <div className="text-center py-5"><Spinner /></div>;

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h1 className="h3 mb-0">Users</h1>
        <Button color="primary" onClick={() => setCreating({ ...emptyUser })}>Add user</Button>
      </div>

      {error && <Alert fade={false} color="danger" toggle={() => setError(null)}>{error}</Alert>}

      <Table hover className="align-middle">
        <thead>
          <tr>
            <th>Name</th>
            <th>Email</th>
            <th className="text-center">Role</th>
            <th className="text-center">Status</th>
            <th className="text-end">Actions</th>
          </tr>
        </thead>
        <tbody>
          {users.map((user) => (
            <tr key={user.id}>
              <td>{user.displayName}</td>
              <td className="text-muted small">{user.email}</td>
              <td className="text-center">
                {user.isAdmin ? <Badge color="primary">Admin</Badge> : <span className="text-muted small">Player</span>}
              </td>
              <td className="text-center">
                {user.isActive ? <Badge color="success">Active</Badge> : <Badge color="secondary">Disabled</Badge>}
              </td>
              <td className="text-end">
                <Button size="sm" color="link" onClick={() => setEditing({
                  id: user.id,
                  firstName: user.firstName,
                  lastName: user.lastName,
                  email: user.email,
                  isAdmin: user.isAdmin,
                  isActive: user.isActive,
                  enableNotifications: user.enableNotifications,
                  newPassword: '',
                })}>Edit</Button>
                {user.id !== me?.id && (
                  <Button size="sm" color="link" className="text-danger" onClick={() => remove(user)}>Delete</Button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      <Modal isOpen={creating !== null} toggle={() => setCreating(null)}>
        <Form onSubmit={submitCreate}>
          <ModalHeader toggle={() => setCreating(null)}>Add user</ModalHeader>
          <ModalBody>
            <FormGroup>
              <Label for="c-first">First name</Label>
              <Input id="c-first" value={creating?.firstName ?? ''} required
                onChange={(e) => setCreating({ ...creating, firstName: e.target.value })} />
            </FormGroup>
            <FormGroup>
              <Label for="c-last">Last name</Label>
              <Input id="c-last" value={creating?.lastName ?? ''} required
                onChange={(e) => setCreating({ ...creating, lastName: e.target.value })} />
            </FormGroup>
            <FormGroup>
              <Label for="c-email">Email</Label>
              <Input id="c-email" type="email" value={creating?.email ?? ''} required
                onChange={(e) => setCreating({ ...creating, email: e.target.value })} />
            </FormGroup>
            <FormGroup>
              <Label for="c-pass">Password</Label>
              <Input id="c-pass" type="password" value={creating?.password ?? ''} minLength={8} required
                onChange={(e) => setCreating({ ...creating, password: e.target.value })} />
            </FormGroup>
            <FormGroup check>
              <Input id="c-admin" type="checkbox" checked={creating?.isAdmin ?? false}
                onChange={(e) => setCreating({ ...creating, isAdmin: e.target.checked })} />
              <Label for="c-admin" check>Administrator</Label>
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" type="button" onClick={() => setCreating(null)}>Cancel</Button>
            <Button color="primary" type="submit" disabled={busy}>
              {busy ? <Spinner size="sm" /> : 'Create'}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>

      <Modal isOpen={editing !== null} toggle={() => setEditing(null)}>
        <Form onSubmit={submitEdit}>
          <ModalHeader toggle={() => setEditing(null)}>Edit user</ModalHeader>
          <ModalBody>
            <FormGroup>
              <Label for="e-first">First name</Label>
              <Input id="e-first" value={editing?.firstName ?? ''} required
                onChange={(e) => setEditing({ ...editing, firstName: e.target.value })} />
            </FormGroup>
            <FormGroup>
              <Label for="e-last">Last name</Label>
              <Input id="e-last" value={editing?.lastName ?? ''} required
                onChange={(e) => setEditing({ ...editing, lastName: e.target.value })} />
            </FormGroup>
            <FormGroup>
              <Label for="e-email">Email</Label>
              <Input id="e-email" type="email" value={editing?.email ?? ''} required
                onChange={(e) => setEditing({ ...editing, email: e.target.value })} />
            </FormGroup>
            <FormGroup>
              <Label for="e-pass">New password</Label>
              <Input id="e-pass" type="password" value={editing?.newPassword ?? ''}
                placeholder="Leave blank to keep current"
                onChange={(e) => setEditing({ ...editing, newPassword: e.target.value })} />
            </FormGroup>
            <FormGroup check className="mb-2">
              <Input id="e-admin" type="checkbox" checked={editing?.isAdmin ?? false}
                onChange={(e) => setEditing({ ...editing, isAdmin: e.target.checked })} />
              <Label for="e-admin" check>Administrator</Label>
            </FormGroup>
            <FormGroup check>
              <Input id="e-active" type="checkbox" checked={editing?.isActive ?? true}
                onChange={(e) => setEditing({ ...editing, isActive: e.target.checked })} />
              <Label for="e-active" check>Active (can sign in)</Label>
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" type="button" onClick={() => setEditing(null)}>Cancel</Button>
            <Button color="primary" type="submit" disabled={busy}>
              {busy ? <Spinner size="sm" /> : 'Save'}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>
    </div>
  );
}
